import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, ShieldCheck, PlayCircle } from 'lucide-react';
import { vevo } from '@/lib/api';
import { toast } from 'sonner';


const VevoFld = ({ label, val, onChange, type = 'text', testid, placeholder }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
    <Input type={type} value={val} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
  </div>
);

export default function VevoPanel({ matterRef }) {
  const [checks, setChecks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    passport_number: '', dob: '', country_code: '',
    immi_grant_letter_number: '', immi_application_id: '',
  });

  const load = React.useCallback(() => {
    setLoading(true);
    vevo.list(matterRef).then(setChecks).finally(() => setLoading(false));
  }, [matterRef]);
  React.useEffect(load, [load]);

  const runCheck = async () => {
    if (!form.passport_number.trim()) { toast.error('Passport number required'); return; }
    setRunning(true);
    try {
      const payload = { matter_ref: matterRef };
      Object.entries(form).forEach(([k, v]) => { if (v) payload[k] = v; });
      await vevo.check(payload);
      toast.success('VEVO check complete');
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'VEVO check failed');
    } finally { setRunning(false); }
  };

  const Fld = VevoFld;

  return (
    <Card className="border-slate-200" data-testid="vevo-panel">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#2ECC9A]" />
            <div className="text-sm font-semibold text-[#1A3A5C]">VEVO check</div>
            {checks[0]?.stub && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[10px]">Sandbox</Badge>}
          </div>
          <Button data-testid="vevo-run-check-btn" onClick={() => setOpen(true)}
                  size="sm"
                  className="h-8 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            <PlayCircle className="h-3 w-3"/> Run check
          </Button>
        </div>

        {loading && <div className="mt-4 text-xs text-slate-400"><Loader2 className="inline h-3 w-3 animate-spin"/> Loading…</div>}
        {!loading && checks.length === 0 && (
          <div className="mt-4 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
            No VEVO checks yet. Provide the applicant&rsquo;s passport, IMMI grant letter, and application ID to verify visa entitlement.
          </div>
        )}
        {checks.slice(0, 3).map((c) => (
          <div key={c.id} data-testid={`vevo-check-${c.id}`}
               className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-[#1A3A5C]">
                {c.result?.visa_class}
                {c.result?.status && <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">{c.result.status}</Badge>}
              </div>
              <div className="text-[10px] text-slate-500">{new Date(c.checked_at).toLocaleString('en-AU')}</div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-700">
              <div><span className="text-slate-400">Grant date:</span> {c.result?.grant_date || '—'}</div>
              <div><span className="text-slate-400">Expiry:</span> <span className="font-medium">{c.result?.expiry_date || '—'}</span></div>
              <div><span className="text-slate-400">Entries:</span> {c.result?.entries_allowed || '—'}</div>
              <div><span className="text-slate-400">Work:</span> {c.result?.work_entitlement || '—'}</div>
              {c.result?.passport_number && <div><span className="text-slate-400">Passport:</span> <span className="font-mono">{c.result.passport_number}</span></div>}
              {c.result?.immi_grant_letter_number && <div><span className="text-slate-400">IMMI grant #:</span> <span className="font-mono">{c.result.immi_grant_letter_number}</span></div>}
              {c.result?.immi_application_id && <div className="col-span-2"><span className="text-slate-400">IMMI app ID:</span> <span className="font-mono">{c.result.immi_application_id}</span></div>}
            </div>
            {c.result?.conditions?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {c.result.conditions.map((cond, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] border-slate-300 text-slate-600">{cond}</Badge>
                ))}
              </div>
            )}
          </div>
        ))}

        <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="vevo-form-dialog">
            <DialogHeader><DialogTitle>Run VEVO check</DialogTitle></DialogHeader>
            <div className="text-xs text-slate-500">Provide the details from the applicant&rsquo;s passport, most recent IMMI grant letter, and IMMI application ID.</div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Fld label="Passport number" val={form.passport_number} onChange={(v) => setForm({ ...form, passport_number: v })} testid="vevo-passport" />
              <Fld label="Date of birth" val={form.dob} onChange={(v) => setForm({ ...form, dob: v })} type="date" testid="vevo-dob" />
              <Fld label="Country code (3-letter)" val={form.country_code} onChange={(v) => setForm({ ...form, country_code: v.toUpperCase() })} placeholder="IND, CHN, GBR…" testid="vevo-country" />
              <Fld label="IMMI grant letter #" val={form.immi_grant_letter_number} onChange={(v) => setForm({ ...form, immi_grant_letter_number: v })} testid="vevo-immi-grant" />
              <Fld label="IMMI application ID" val={form.immi_application_id} onChange={(v) => setForm({ ...form, immi_application_id: v })} testid="vevo-immi-app" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={runCheck} disabled={running} data-testid="vevo-submit">
                {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}Run VEVO
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
